import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
const _excluded = ["pageName"],
  _excluded2 = ["to", "pageName"],
  _excluded3 = ["to", "pageName"];
import PropTypes from 'prop-types';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIRouterTabs from 'ui-addon-react-router-dom/UIRouterTabs';
import UIRouterTab from 'ui-addon-react-router-dom/UIRouterTab';
import { MYLEARNING_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT, PAGE_NAMES, PARTNER_TRACKS_URL_FRAGMENT, WATCH_LATER_URL_FRAGMENT } from '../../../constants/Navigation';
import UsageTracker from '../../../utils/UsageTracker';
import { useIsPartner } from '../../partners';
import UIRouterNavLink from 'ui-addon-react-router-dom/UIRouterNavLink';
import HubSpotLogo from '../../../components/Icons/HubSpotLogo';
import { useNavigationState } from '../navbarQueries';
import UITab from 'UIComponents/nav/UITab';
import styled, { theme, css, mediaQuery } from 'academy-common-lib/utils/Styling';
import { NewBadge } from './DesktopDrowndown';
import { NavbarSavedItemsTooltipText, useShowNavbarPlaylistsTooltip, useShowNavbarWatchLaterTooltip } from '../../savedItems';
import { useHasGate } from '../../accounts';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledUIRouterTabs = styled(UIRouterTabs).withConfig({
  displayName: "NavbarTabs__StyledUIRouterTabs",
  componentId: "sc-1bh0ugv-0"
})(["background:", ";padding-right:0;flex-grow:1;", " & div[data-content='true'][role='navigation']{border-bottom:none;}.uiDropdown__button{&,&:active,&:hover{color:", ";text-decoration:none;}}"], theme.colors.PANTERA, mediaQuery('mobile', css(["padding:0;"])), theme.colors.OLAF);
// Increase specificity to override color in UITab
// https://git.hubteam.com/HubSpot/UIComponents/blob/7b26717/UIComponents/static/js//nav/UITab.js#L170
const StyledUIRouterTab = styled(UIRouterTab).withConfig({
  displayName: "NavbarTabs__StyledUIRouterTab",
  componentId: "sc-1bh0ugv-1"
})(["padding:12px 16px !important;&&,&&:hover{color:", ";font-weight:400;}&&:hover{background-color:", ";}& > span{display:none;}", ""], theme.colors.OLAF, theme.colors.FLAT_EARTH, props => props.$active && css(["background:", ";"], theme.colors.FLAT_EARTH));
const StyledUIRouterNavLink = styled(UIRouterNavLink).withConfig({
  displayName: "NavbarTabs__StyledUIRouterNavLink",
  componentId: "sc-1bh0ugv-2"
})(["&&,&&:hover{color:", ";font-weight:400;}&&:hover{background-color:", " !important;}background:", " !important;", " position:relative;"], theme.colors.OLAF, theme.colors.FLAT_EARTH, theme.colors.PANTERA, props => props.$active && css(["background:", " !important;"], theme.colors.FLAT_EARTH));
const StyledUITab = styled(_ref => {
  let props = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
  // @ts-expect-error custom element
  return /*#__PURE__*/_jsx(UITab, Object.assign({}, props, {
    Link: "div"
  }));
}).withConfig({
  displayName: "NavbarTabs__StyledUITab",
  componentId: "sc-1bh0ugv-3"
})(["padding:", ";&&,&&:hover{color:", ";}&&:hover{background-color:", ";}& > span{display:none;}background-color:", " !important;", " position:relative;"], props => props.$padding === 'none' ? '0' : '12px 28px', theme.colors.OLAF, theme.colors.FLAT_EARTH, theme.colors.PANTERA, props => props.$active && css(["background-color:", " !important;"], theme.colors.FLAT_EARTH));
const ConnectedStyledUITab = _ref2 => {
  let {
      pageName
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const {
    isActive
  } = useNavigationState(pageName);
  return /*#__PURE__*/_jsx(StyledUITab, Object.assign({}, props, {
    $active: isActive,
    "data-tab-active": isActive,
    role: "presentation"
    // @ts-expect-error it doesn't pass the tabIndex prop properly
    ,
    tabindex: -1
  }));
};
const BacklinkUIRouterTab = _ref3 => {
  let {
      to,
      pageName
    } = _ref3,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded2);
  const {
    backLink,
    isActive,
    hasNavigatedFromNavbarItem
  } = useNavigationState(pageName);
  return /*#__PURE__*/_jsx(StyledUIRouterTab, Object.assign({}, props, {
    $active: isActive,
    to: hasNavigatedFromNavbarItem ? backLink : to
  }));
};

/**
 * Including tabId & title PropTypes to silence UIComponents validation noise
 * in console:
 *
 * "Warning: Failed prop type: Controllable(UITabs): All children must be
 * instances of UITab."
 *
 * See https://hubspot.slack.com/archives/C1WG3L3J6/p1573133826232300
 * See https://git.hubteam.com/HubSpot/UIComponents/blob/892bb7ef/UIComponents/static/js//nav/UITabs.js#L37-L40
 */
const psuedoUITab = {
  tabId: PropTypes.string,
  title: PropTypes.object
};
BacklinkUIRouterTab.propTypes = psuedoUITab;
ConnectedStyledUITab.propTypes = psuedoUITab;
const BacklinkUIRouterNavLink = _ref4 => {
  let {
      to,
      pageName
    } = _ref4,
    props = _objectWithoutPropertiesLoose(_ref4, _excluded3);
  const {
    isActive
  } = useNavigationState(pageName);
  return /*#__PURE__*/_jsx(StyledUIRouterNavLink, Object.assign({}, props, {
    $active: isActive,
    to: to
  }));
};
const NavbarTabs = () => {
  const showNavbarWatchLaterTooltip = useShowNavbarWatchLaterTooltip();
  const showNavbarPlaylistsTooltip = useShowNavbarPlaylistsTooltip();
  const isPartner = useIsPartner();
  const hasShortsGate = useHasGate('Academy:ShortFormContent');
  const onNavItemClick = trackingAction => {
    UsageTracker.track('navInteraction', {
      action: trackingAction
    });
  };
  return /*#__PURE__*/_jsxs(UIFlex, {
    style: {
      paddingLeft: '12px'
    },
    children: [/*#__PURE__*/_jsx(BacklinkUIRouterNavLink, {
      "data-onboarding": "academy-logo",
      exact: false,
      onClick: () => onNavItemClick('hubspot-academy-clicked'),
      pageName: PAGE_NAMES.CATALOG,
      style: {
        flexShrink: 0,
        padding: '12px 16px'
      },
      to: "/",
      use: "unstyled",
      children: /*#__PURE__*/_jsx("p", {
        style: {
          margin: 0,
          fontSize: '19px',
          fontWeight: 400
        },
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "academyNav.heading.homepageWithLogo",
          options: {
            hubspotTextLogo: /*#__PURE__*/_jsx(HubSpotLogo, {})
          }
        })
      })
    }), /*#__PURE__*/_jsxs(StyledUIRouterTabs, {
      children: [/*#__PURE__*/_jsx(BacklinkUIRouterTab, {
        "data-test-id": "nav-tab-learnings",
        exact: false,
        onClick: () => onNavItemClick('my-learning-clicked'),
        pageName: PAGE_NAMES.MY_LEARNING,
        strict: false,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyNav.heading.myLearning"
        }),
        to: `${MYLEARNING_URL_FRAGMENT}${hasShortsGate ? '' : '/in-progress'}`
      }), /*#__PURE__*/_jsx(BacklinkUIRouterTab, {
        "data-test-id": "nav-tab-watch-later",
        exact: false,
        onClick: () => onNavItemClick('watch-later-clicked'),
        pageName: PAGE_NAMES.SAVED_ITEMS,
        strict: false,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyNav.heading.watchLater"
        }),
        to: WATCH_LATER_URL_FRAGMENT,
        tooltip: /*#__PURE__*/_jsx(NavbarSavedItemsTooltipText, {}),
        tooltipProps: {
          open: showNavbarWatchLaterTooltip
        }
      }), /*#__PURE__*/_jsx(BacklinkUIRouterTab, {
        exact: false,
        onClick: () => onNavItemClick('my-playlists-clicked'),
        pageName: PAGE_NAMES.PLAYLISTS,
        strict: false,
        title: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyNav.heading.myPlaylists"
          }), /*#__PURE__*/_jsx(NewBadge, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyNav.new"
            })
          })]
        }),
        to: MY_PLAYLISTS_URL_FRAGMENT,
        tooltip: /*#__PURE__*/_jsx(NavbarSavedItemsTooltipText, {}),
        tooltipProps: {
          open: showNavbarPlaylistsTooltip,
          maxWidth: 285
        }
      }), /*#__PURE__*/_jsx(BacklinkUIRouterTab, {
        "data-test-id": "nav-tab-team",
        exact: false,
        onClick: () => onNavItemClick('my-team-clicked'),
        pageName: PAGE_NAMES.TEAM,
        strict: false,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyNav.heading.myTeam"
        }),
        to: '/team'
      }), isPartner && /*#__PURE__*/_jsx(BacklinkUIRouterTab, {
        "data-test-id": "nav-tab-partners",
        exact: false,
        onClick: () => onNavItemClick('partner-clicked'),
        pageName: PAGE_NAMES.PARTNER,
        strict: false,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyNav.heading.partners"
        }),
        to: PARTNER_TRACKS_URL_FRAGMENT
      })]
    })]
  });
};
export default NavbarTabs;